import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {Alert, Button, Icon, Input, Spacer, Text, Theme, Toggle} from "@dspworkplace/ui";
import Tooltip from "../../../../components/Tooltip";
import {Link} from "../../../../components/UI";

function CopyToClipboardButton({
    text,
    ...props
}) {
    const [copied, setCopied] = useState(false);

    async function copyToClipboard(textToCopy) {
        // Navigator clipboard api needs a secure context (https)
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } else {
            // Use the 'out of viewport hidden text area' trick
            const textArea = document.createElement("textarea");
            textArea.value = textToCopy;

            // Move textarea out of the viewport so it's not visible
            textArea.style.position = "absolute";
            textArea.style.left = "-999999px";

            document.body.prepend(textArea);
            textArea.select();

            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (error) {

            } finally {
                textArea.remove();
            }
        }
    }

    return (
        <Tooltip content={!copied ? 'Copy to clipboard' : 'Copied!'}>
            <Button
                // size='small'
                Type='button'
                type={"default"}
                {...props}
                onClick={() => copyToClipboard(text)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    top: 26,
                    marginLeft: 8,
                    backgroundColor: copied ? Theme.colors.success.bg : null,
                    ...props?.style
                }}
            >
                {!copied ? (
                    <Icon.Copy size={'20px'} />
                ) : (
                    <Icon.Check size={'20px'} color={Theme.colors.success.text} />
                )}
            </Button>
        </Tooltip>
    );
}

export default function ADPSettingsV2({  initialValues = false, onSubmit }) {
    const [loading, setLoading] = useState(false);
    const { register, handleSubmit, reset, watch, errors, getValues } = useForm({
        defaultValues: { connectorCode: randomStringGenerator(36) }
    });

    useEffect(() => { if (initialValues) reset(initialValues) }, [initialValues]);

    const isAdpEnable = watch("enabled");

    function randomStringGenerator(len) {
        let length = (len) ? (len) : (20);
        let string = "abcdefghijklmnopqrstuvwxyz0123456789";
        let password = "";
        while (password.length < length) {
            let entity1 = Math.ceil(string.length * Math.random() * Math.random());
            let hold = string.charAt(entity1);
            password += hold;
        }

        return password.substring(0, len).toUpperCase();
    }

    const handleSave = async (data) => {
        data.credentials = {}
        data.credentials.connector_code = data.connectorCode
        data.credentials.unique_identifier = data.uniqueAccountIdentifier

        delete data.connectorCode
        delete data.uniqueAccountIdentifier

        setLoading(true)

        onSubmit(data)
    }

    return (
        <form onSubmit={handleSubmit(handleSave)}>
            <div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 60,
            }}>
                <div style={{flexShrink: 0}}>
                    <input type='hidden' name='id' ref={register} autoComplete="off"/>
                    <input type='hidden' value='ADP' name='integration_type' ref={register} autoComplete="off"/>
                    <Input
                        ref={register({required: true})}
                        name='name'
                        label='Account name'
                        Required={true}
                        invalid={errors && errors.name}
                        error={errors && errors.name && 'Required field'}
                        autoComplete="off"
                        help={'You can have multiple accounts, so this helps to identify which account is supplying the data'}
                    />
                    <Spacer bottom={3}/>
                    <Toggle
                        ref={register}
                        name="enabled"
                        // help="If this is enabled, the system will automatically fetch ADP punch data."
                        singleText="Enable this account"
                        on={isAdpEnable}
                    />
                    <Spacer bottom={3} />
                    <Toggle
                        ref={register}
                        name="syncTimeOff"
                        // help="If this is enabled, the system will automatically fetch ADP punch data."
                        singleText="Enable TimeOff Sync"
                    />
                    <Spacer bottom={5}/>
                    <Input
                        ref={register}
                        name="connectorCode"
                        label="ADP Connector Code"
                        readOnly
                        onClick={e => e.target.select()}
                    />
                    <CopyToClipboardButton text={watch('connectorCode')}/>
                    <Spacer bottom={3}/>
                    <Input
                        ref={register}
                        name="uniqueAccountIdentifier"
                        label="Unique Account Identifier"
                        readOnly
                    />
                    <Spacer bottom={5}/>
                </div>

                <Spacer
                    all={5}
                    style={{
                        background: `${Theme.colors.info.bg}55`,
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: `${Theme.colors.info.border}55`,
                        borderRadius: Theme.defaultRadius,
                    }}
                >
                    <Text>
                        Integrate your ADP employees, punch times and time offs using our new
                        {' '}<Link href={'https://apps.adp.com/en-US/apps/595824'} target='_blank'>ADP Marketplace Connector APP</Link>.
                        <br/>
                        <br/>
                        Once you have added the dspworkplace marketplace app to your ADP account:
                        <br/>
                        • <Link href={'https://apps.adp.com/account/apps'} target='_blank'>Manage your ADP billing in here</Link>
                        <br/>
                        • <Link href={'https://apps.adp.com/account/users'} target='_blank'>Manage ADP user management within the app here</Link>
                    </Text>
                </Spacer>
            </div>

            {watch('uniqueAccountIdentifier') && (
                <Spacer bottom={5} style={{display: "inline-flex"}}>
                    <Alert.Warning
                        useIcon
                        title={'Client Data Access Authorization'}
                        content={<>
                            ADP requires that the DSP owner grants the dspworkplace Marketplace Connector APP
                            consent to
                            access the employee data.
                            <br/>
                            Use <Link
                            style={{fontSize: 'inherit'}}
                            target='_blank'
                            href='https://adpapps.adp.com/consent-manager/pending/email?consumerApplicationID=e29bad64-6e74-4518-a7a4-134a23e36eaf'
                        >this link</Link> to provide consent if not done yet.
                        </>}
                    />
                </Spacer>
            )}

            <Button
                type='primary'
                disabled={loading}
            >
                {!loading ? 'Save' : 'Saving...'}
            </Button>
        </form>
    );
}
